import {
  InfiniteQueryObserver,
  type InfiniteData,
  type QueryClient,
  type QueryFunction,
  type QueryKey,
  type skipToken,
} from "@tanstack/react-query";
import { runInAction } from "mobx";
import { getQueryClient } from "@/queries/client";
import { patchScoped } from "@/queries/invalidation";

/**
 * The store side of a keyset feed on the query cache (NG-2544): the project
 * activity feed (R3) and comments (R4). With the `query-cache` flag on, a feed
 * store reads through the infinite query and projects its pages into the
 * observable map its components already render; with it off, nothing here runs.
 */

/** One page of a newest-first keyset feed, as the API returns it. */
interface FeedPage<T> {
  results: T[];
  next_cursor: string | null;
}

/** The row type of a feed page. */
type RowOf<TPage extends FeedPage<unknown>> = TPage["results"][number];

/**
 * A feed's infinite query, as `app/queries/project-resources.ts` builds it.
 * Only what these helpers read is named; the page type is taken from the
 * query function, and the client calls get the options as built.
 */
export interface FeedQuery<TPage> {
  queryKey: QueryKey;
  staleTime?: unknown;
  queryFn?: QueryFunction<TPage, QueryKey, string | null> | typeof skipToken;
}

/** Whether a read of `query` would be answered from cache, with no request. */
function isFeedFresh<TPage>(client: QueryClient, query: FeedQuery<TPage>): boolean {
  const entry = client.getQueryCache().find({ queryKey: query.queryKey, exact: true });
  return (
    entry?.state.data !== undefined &&
    !entry.isStaleByTime(typeof query.staleTime === "number" ? query.staleTime : 0)
  );
}

/**
 * Read the feed. A fresh entry answers with no request, and concurrent readers
 * share one. `force` is the store's `fetch`: the newest page now, with the
 * older pages dropped, as the flag-off path replaces them.
 */
function readFeed<TPage>(
  client: QueryClient,
  query: FeedQuery<TPage>,
  { force }: { force: boolean },
): Promise<InfiniteData<TPage, string | null>> {
  if (!force) {
    return client.fetchInfiniteQuery(query as never) as Promise<InfiniteData<TPage, string | null>>;
  }
  client.setQueryData<InfiniteData<TPage, string | null>>(query.queryKey, (data) =>
    data ? { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) } : data,
  );
  return client.fetchInfiniteQuery({ ...query, staleTime: 0 } as never) as Promise<
    InfiniteData<TPage, string | null>
  >;
}

/** Every row of the feed's pages, newest first. */
function feedRows<T>(data: InfiniteData<FeedPage<T>, unknown>): T[] {
  return data.pages.flatMap((page) => page.results);
}

/** The cursor for the next older page; `null` once the feed is exhausted. */
function feedCursor<T>(data: InfiniteData<FeedPage<T>, unknown>): string | null {
  return data.pages.at(-1)?.next_cursor ?? null;
}

/** How a feed store shows a cached read: its loading flag and its projection. */
export interface FeedProjection<T> {
  setLoading: (loading: boolean) => void;
  apply: (rows: T[], cursor: string | null) => void;
}

/**
 * Read the feed through the cache and project it into the store. The loading
 * state shows only when a request is made, so a fresh entry does not flash it.
 */
export async function readFeedInto<TPage extends FeedPage<unknown>>(
  query: FeedQuery<TPage>,
  { force }: { force: boolean },
  { setLoading, apply }: FeedProjection<RowOf<TPage>>,
): Promise<RowOf<TPage>[]> {
  const client = getQueryClient();
  const requests = force || !isFeedFresh(client, query);
  if (requests) runInAction(() => setLoading(true));
  try {
    const data = await readFeed(client, query, { force });
    const rows = feedRows(data) as RowOf<TPage>[];
    runInAction(() => apply(rows, feedCursor(data)));
    return rows;
  } finally {
    if (requests) runInAction(() => setLoading(false));
  }
}

/**
 * Append the next older page to the cached feed and project it. Answers
 * `false` when nothing is cached to append to, and the caller then reads the
 * way the flag-off path does.
 */
export async function readNextFeedPageInto<TPage extends FeedPage<unknown>>(
  query: FeedQuery<TPage>,
  apply: FeedProjection<RowOf<TPage>>["apply"],
): Promise<boolean> {
  const client = getQueryClient();
  if (client.getQueryData(query.queryKey) === undefined) return false;
  const observer = new InfiniteQueryObserver(client, query as never);
  const result = await observer.fetchNextPage({ throwOnError: true });
  const data = result.data as InfiniteData<TPage, string | null> | undefined;
  if (!data) return false;
  runInAction(() => apply(feedRows(data) as RowOf<TPage>[], feedCursor(data)));
  return true;
}

/** Rewrite the rows of a cached feed in place, if it is cached. */
export function patchFeed<T>(
  client: QueryClient,
  key: QueryKey,
  update: (results: T[], pageIndex: number) => T[],
): void {
  patchScoped<InfiniteData<FeedPage<T>, unknown>>(client, key, (data) => ({
    ...data,
    pages: data.pages.map((page, index) => ({ ...page, results: update(page.results, index) })),
  }));
}
